import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CNavLink } from '../CNavLink'; // Assuming CNavLink is directly in components or adjust path
import NotificationBadge from './NotificationBadge.jsx'; // Path relative to common/
import { PersonalMenu } from './PersonalMenu.jsx'; // Path relative to common/
import UserAvatar from './UserAvatar'; // Import UserAvatar component
import playerImage from '../../assets/profile.png'; // Adjust path as needed relative to common/
import '../../styles/avatar.css'; // Import avatar styles
import { useActiveLeague } from '../../hooks/useActiveLeague';
import { buildLeagueTableLink } from '../../utils/leagueLinks';
import { SignupGateModal } from '../auth/SignupGateModal';
import { useSuppressTutorialTips } from '../../context/TutorialContext';
import { QuickplayStepper } from '../quickplay/QuickplayStepper';

// Note: This component needs props passed down from Layout:
// user, logout, showPersonal, setShowPersonal, showChat, setShowChat, handleLeagueNavigation, isLoadingLeagues, hasLeagues, leagueSlug
export function Header({
    user,
    logout, // Assuming logout function is passed
    showPersonal,
    setShowPersonal,
    showChat, // Added showChat prop
    setShowChat, // Added setShowChat prop
    handleLeagueNavigation,
    isLoadingLeagues, // Need these props for league link logic
    hasLeagues,
    leagueSlug,
    currentTeamId,
    isLeagueSelectPage,
    hideMainNav = false, // Quick Play funnel takes over the menu slot
    quickplayMatchId = null,
    isQuickplayMatchStarted = false,
    isQuickplayMatchCompleted = false,
}) {
    const { activeLeague } = useActiveLeague();
    const location = useLocation();
    const activeLeagueSlug = activeLeague?.slug;
    const resolvedLeagueSlug = activeLeagueSlug || leagueSlug;
    // Quick Play leagues show match history behind the league link, so the label
    // has to match. The slug prefix covers the early stub state where the active
    // league is known by slug only and gameMode has not loaded yet.
    const isQuickplayLeague = activeLeague?.gameMode === 'quickplay' ||
        (!activeLeague?.gameMode && String(resolvedLeagueSlug || '').startsWith('quick-play-'));
    const leagueLinkLabel = isQuickplayLeague ? 'Matches' : 'League';
    const isGuestUser = user?.isGuest === true;
    const [isSaveGateOpen, setIsSaveGateOpen] = useState(false);
    // The funnel bar lives in the same slot as the menu links, so it sits at the
    // top on desktop and on the bottom bar on mobile.
    const showQuickplayStepper = hideMainNav && !isLeagueSelectPage && !!resolvedLeagueSlug;
    const showMainNav = !hideMainNav && !isLeagueSelectPage;

    // Guests get no browser warning before a tab closes, so the header keeps a
    // permanent way to save. Hide tutorial tips while that gate is open.
    useSuppressTutorialTips(isSaveGateOpen);

    // Example PageAlert logic, maybe kept in Layout or passed as prop/child
    const PageAlert = ({ message }) => {
         if (!message || !message.text) {
            return null;
        }
        return (
            <div className="page-alert">
                {message.text}
                {message.link && (
                    <Link
                        className="alert-link icon-arrow-right"
                        to={message.link}
                    >
                    </Link>
                )}
            </div>
        );
    };

    // We might pass a simpler message object or handle PageAlert in Layout itself
    const message = {}; // Placeholder, original message state was in Layout

    return (
        <header>
            <nav id='menu' className='main-menu'>
                {/* Consider if PageAlert belongs here or in Layout */}
                {/* <PageAlert message={message} /> */}

                <div className={`wrapper ${showQuickplayStepper ? 'quickplay-funnel-wrapper' : ''}`}>
                    <div className='logo-block'>
                        {/* Logic for logo link based on auth/team status */}
                        <CNavLink 
                            to={currentTeamId && leagueSlug ? `/${leagueSlug}/team-pitch/${currentTeamId}` : (user ? (hasLeagues ? '/switch-league' : '/onboarding') : '/login')}
                            className='logo icon-logo-beta'>
                        </CNavLink>
                    </div>

                    <div className={`nav-block ${isLeagueSelectPage ? 'desktop-block' : ''} ${showQuickplayStepper ? 'quickplay-funnel-block' : ''}`}>
                        {showQuickplayStepper && (
                            <QuickplayStepper
                                leagueSlug={resolvedLeagueSlug}
                                currentTeamId={currentTeamId}
                                matchId={quickplayMatchId}
                                isMatchStarted={isQuickplayMatchStarted}
                                isMatchCompleted={isQuickplayMatchCompleted}
                            />
                        )}

                        {showMainNav && (
                            <ul className={currentTeamId ? "main-nav " : "main-nav three-cols"}>
                                {/* My Team Link */}
                                {user && currentTeamId && (
                                     <CNavLink to={leagueSlug ? `/${leagueSlug}/team-pitch/${currentTeamId}` : (hasLeagues ? '/switch-league' : '/onboarding')} active={['team-pitch', 'team-list', 'team-settings']}>
                                         <span className='ico icon-team'></span>
                                         My team
                                     </CNavLink>
                                 )}
                                
                                {/* My League Link - Shows current team's league if they have one */}
                                {user && resolvedLeagueSlug && (
                                    <CNavLink
                                        to={buildLeagueTableLink(resolvedLeagueSlug)}
                                        active={['/table', '/fixtures', '/results']}
                                        onClick={activeLeagueSlug ? undefined : handleLeagueNavigation}
                                    >
                                        <span className='ico icon-league'></span>
                                        {leagueLinkLabel}
                                    </CNavLink>
                                )}
                                
                                {/* Transfers Link */}
                                {user && (
                                     <CNavLink to={leagueSlug ? `/${leagueSlug}/transfers-players` : (hasLeagues ? '/switch-league' : '/onboarding')} active={['transfers']}>
                                         <span className='ico icon-transfers'></span>
                                         Transfers
                                     </CNavLink>
                                 )}
                                
                                {/* More Link - Quick Play / League mode hub */}
                                {user && (
                                    <CNavLink to="/more" activeExact={['/more']}>
                                        <span className='ico icon-frame'></span>
                                        More
                                    </CNavLink>
                                )}
                            </ul>
                        )}
                    </div>

                    <div className="ctrls-block">
                        {/* Always-on save prompt for guests */}
                        {isGuestUser && (
                            <button
                                type="button"
                                className="save-progress-btn"
                                onClick={() => setIsSaveGateOpen(true)}
                            >
                                Save progress
                            </button>
                        )}

                        {/* Notification Badge - guests have nothing to be notified about yet */}
                        {user && !isGuestUser && <NotificationBadge currentTeamId={currentTeamId} />}

                        {/* Chat button removed temporarily until feature is implemented */}
                        {/*
                        {user && (
                            <button 
                                type="button" 
                                className="ctrl-btn icon-message" // Example class
                                onClick={() => setShowChat(true)} // Assuming this toggles chat visibility
                                style={{ marginRight: '10px' }} // Example spacing
                            >
                            </button>
                        )}
                        */}

                        {/* Profile/Personal Menu Button - hidden until a guest signs up */}
                        {user && !isGuestUser && (
                             <button type="button" className="profile-btn profile-nav-btn"
                                     onClick={(event) => {event.stopPropagation(); setShowPersonal(!showPersonal)}}
                             >
                                 <div className="image-block">
                                     <UserAvatar 
                                       avatarUrl={user.avatar} 
                                       userName={user.username}
                                       className="header-avatar" 
                                       size="small"
                                     />
                                 </div>
                                 <span className="profile-nav-name">
                                     {user?.username}
                                 </span>
                                 {/* PersonalMenu likely needs logout passed to it */}
                                 <PersonalMenu showPersonal={showPersonal} setShowPersonal={setShowPersonal} logout={logout} />
                             </button>
                         )}
                    </div>
                </div>
            </nav>

            {isSaveGateOpen && (
                <SignupGateModal
                    title="Keep your progress"
                    description="Create your free account to keep your team and match history."
                    redirectTo={`${location.pathname}${location.search}`}
                    onDismiss={() => setIsSaveGateOpen(false)}
                />
            )}
        </header>
    );
}
